import * as Sentry from "@sentry/react";
import type { ApiError, ErrorType } from "../hooks/useApi";

// Erros de produção vão para o Sentry. Sem REACT_APP_SENTRY_DSN (dev, testes) nada é enviado.
// Dado de paciente não sai daqui: as URLs perdem e-mail, nome, token e query, e o erro
// da API vai só com tipo, status e request_id (nunca o texto do backend).

// Só o que é defeito nosso ou do backend; 4xx e "sem internet" são esperados.
// Exceção: estes 4xx só acontecem se o front mandou payload ou chave de plano errados.
const REPORTED: ErrorType[] = [
  "unexpected", "unknown", "timeout", "server-unreachable", "stream-interrupted",
  "invalid-payload", "plano-desconhecido", "lookup-key-desconhecida",
];

// De quem é o erro, para filtrar no Sentry.
export function origem(error: ApiError): string {
  if (error.type === "unexpected") return "frontend"; // exceção de JS/render, com stack real
  if (error.requestId) return "backend";             // o backend respondeu: request_id nos logs da AWS
  if (error.type === "unknown") return "gateway";    // HTML do load balancer (502/504), o backend nem viu
  return "rede";                                      // timeout, servidor inalcançável, stream cortado
}

const ID_AFTER = /\/(patients\/:email|convites|convites-acesso|entrar|assinar|checkout)\/[^/]+/g;

export function scrubUrl(url: string): string {
  return url
    .split(/[?#]/)[0]
    .split("/")
    .map((part) => (/@|%40/.test(part) ? ":email" : part))
    .join("/")
    .replace(ID_AFTER, "/$1/:id");
}

export function initMonitoring() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: window.location.hostname,
    release: process.env.REACT_APP_RELEASE || undefined, // commit do deploy (.env.production)
    // O padrão do SDK coleta headers, cookies, corpos e query; aqui nada disso sai.
    dataCollection: {
      userInfo: false, cookies: false, httpHeaders: false, httpBodies: [], urlQueryParams: false, stackFrameVariables: false,
    },
    // O RootLayout já manda os erros soltos para o showError, que reporta; aqui sairiam em dobro.
    integrations: (defaults) => defaults.filter((i) => i.name !== "GlobalHandlers"),
    beforeBreadcrumb(crumb) {
      // Cliques guardam aria-label/title, que podem ter o nome do paciente.
      if (crumb.category?.startsWith("ui.")) return null;
      for (const key of ["url", "from", "to"]) {
        if (typeof crumb.data?.[key] === "string") crumb.data[key] = scrubUrl(crumb.data[key]);
      }
      return crumb;
    },
    beforeSend(event) {
      if (event.request) event.request = { url: event.request.url && scrubUrl(event.request.url) };
      return event;
    },
  });
}

export function reportError(raw: unknown, error: ApiError) {
  if (error.status < 500 && !REPORTED.includes(error.type)) return;
  if (process.env.NODE_ENV === "development") console.error("[erro]", error.type, raw);
  Sentry.withScope((scope) => {
    scope.setTags({ origem: origem(error), type: error.type, status: error.status, request_id: error.requestId });
    scope.setFingerprint([error.type, String(error.status)]);
    // Erro de JS mantém a stack; erro da API vira um Error só com o código.
    Sentry.captureException(raw instanceof Error ? raw : new Error(`${error.type} (${error.status})`));
  });
}
